import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import {
  Banner, Button, DataTable, Details, Dialog, EmptyState, Select,
  StatusChip, TextField, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk, type BackupDestination } from '../lib/api';
import { quandoRelativo } from '../lib/attivita';
import { Copertura } from './backup-copertura';
import { BarraUtente } from '../shell';
import { Archive, Plus, Shield } from '../icons';
import { Anello, CaricoSezione, Logo } from '../brand';
import { t } from '../lib/lingua';
import { ConPermesso } from '../permesso';
import { usePermessi } from '../session';

/**
 * Il backup dell'organizzazione, in due parti che rispondono a due domande
 * diverse.
 *
 * *Chi* ha una copia e di quando — la copertura — è la domanda che si fa chi
 * ha un'azienda, e l'unica riga che conta è quella di un'istanza senza
 * pianificazione: sta prima. *Dove* finiscono le copie — le destinazioni — è
 * configurazione, si guarda una volta e sta sotto.
 */

const TIPI: Record<string, string> = {
  hub_rest: 'Spazio di Molo',
  s3: 'S3 compatibile',
  b2: 'Backblaze B2',
  sftp: 'Server SFTP',
};

/** Un indirizzo d'esempio per tipo: la forma che restic si aspetta, senza prefissi. */
const INDIRIZZO: Record<string, string> = {
  hub_rest: 'https://backup.example.com/meshble/',
  s3: 'https://s3.gra.io.cloud.ovh.net/bucket/meshble',
  b2: 'https://s3.eu-central-003.backblazeb2.com/bucket/meshble',
  sftp: 'utente@host:/backup/meshble',
};

/** La policy IAM della credenziale del server: scrive e legge, cancella solo i lucchetti. */
const POLICY_SOLA_AGGIUNTA = `{
  "Version": "2012-10-17",
  "Statement": [
    { "Effect": "Allow", "Action": ["s3:ListBucket", "s3:GetBucketLocation"],
      "Resource": ["arn:aws:s3:::BUCKET"] },
    { "Effect": "Allow", "Action": ["s3:PutObject", "s3:GetObject"],
      "Resource": ["arn:aws:s3:::BUCKET/*"] },
    { "Effect": "Allow", "Action": ["s3:DeleteObject"],
      "Resource": ["arn:aws:s3:::BUCKET/PERCORSO/locks/*"] }
  ]
}`;

export function BackupPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/backup' });
  const permessi = usePermessi(slug);
  const { istanza } = useSearch({ from: '/shell/orgs/$slug/backup' });
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [apri, setApri] = useState(false);

  const q = useQuery({
    queryKey: ['backup-destinations', slug],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/backup-destinations', { params: { path: { slug } } })),
  });
  const destinazioni = q.data?.destinations ?? [];

  const prova = useMutation({
    mutationFn: (id: string) =>
      must(
        api.POST('/api/v1/orgs/{slug}/backup-destinations/{id}/check', {
          params: { path: { slug, id } },
        }),
      ),
    // L'esito lo tiene l'hub sulla destinazione: si rilegge invece di fidarsi
    // della risposta, così quello che si vede è quello che resta registrato.
    onSettled: () => qc.invalidateQueries({ queryKey: ['backup-destinations', slug] }),
  });

  const predefinita = useMutation({
    mutationFn: (id: string) =>
      mustOk(
        api.PUT('/api/v1/orgs/{slug}/backup-destinations/{id}/default', {
          params: { path: { slug, id } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backup-destinations', slug] });
      avvisa(t('Da ora i nuovi backup vanno qui'));
    },
  });

  return (
    <>
      <TopAppBar
        title={t('Backup')}
        actions={
          <>
            <ConPermesso ha={permessi} serve="backup.destination.manage">
              <Button variant="filled" icon={<Plus />} onClick={() => setApri(true)}>
                {t('Nuova destinazione')}
              </Button>
            </ConPermesso>
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {q.error && (
            <Banner tone="error" title={t('Non riesco a leggere le destinazioni')}>{q.error.message}</Banner>
          )}

          {q.isSuccess && destinazioni.length === 0 && (
            <EmptyState
              icon={<Archive />}
              title={t('Nessun posto dove salvare i backup')}
              action={
                <ConPermesso ha={permessi} serve="backup.destination.manage">
                  <Button variant="filled" icon={<Plus />} onClick={() => setApri(true)}>
                    {t('Scegli dove salvarli')}
                  </Button>
                </ConPermesso>
              }
            >
              {t("I backup di ogni istanza vanno in uno spazio riservato a questa organizzazione, cifrato con una chiave sua. Finché non c'è una destinazione non si può pianificare nessun backup.")}
            </EmptyState>
          )}

          {destinazioni.length > 0 && <Copertura slug={slug} apriIstanza={istanza} />}

          {prova.error && (
            <Banner tone="error" title={t('La prova non è partita')}>{(prova.error as Error).message}</Banner>
          )}

          {destinazioni.length > 0 && (
            <section className="md-stack">
              <div className="md-section-head">
                <h2 className="md-title-medium">{t('Dove vanno i backup')}</h2>
              </div>
              <DataTable
                columns={[
                  { key: 'nome', header: t('Destinazione') },
                  { key: 'tipo', header: t('Tipo') },
                  { key: 'prova', header: t('Ultima prova') },
                  { key: 'azioni', header: '' },
                ]}
                rowKey={(r) => String(r.id)}
                rows={destinazioni.map((d) => ({
                  id: d.id,
                  nome: (
                    <div className="md-due-righe">
                      <span className="md-body-large" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {d.label}
                        {d.isDefault && <StatusChip status="ready" label={t('predefinita')} tone="neutral" />}
                      </span>
                      <span className="md-mono">{d.endpoint}</span>
                    </div>
                  ),
                  tipo: t(TIPI[d.kind] ?? d.kind),
                  prova: <Prova destinazione={d} />,
                  azioni: (
                    <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
                      {/* Ormeggio: si sta stabilendo un contatto con la destinazione. */}
                      <ConPermesso ha={permessi} serve="backup.destination.manage">
                        <Button
                          variant="text"
                          icon={prova.isPending && prova.variables === d.id ? <Logo size={20} anim="ormeggio" tone="inherit" /> : undefined}
                          disabled={prova.isPending}
                          onClick={() => prova.mutate(d.id)}
                        >
                          {prova.isPending && prova.variables === d.id ? t('Provo…') : t('Prova')}
                        </Button>
                      </ConPermesso>
                      {!d.isDefault && (
                        <ConPermesso ha={permessi} serve="backup.destination.manage">
                          <Button
                            variant="text"
                            disabled={predefinita.isPending}
                            onClick={() => predefinita.mutate(d.id)}
                          >
                            {t('Usa questa')}
                          </Button>
                        </ConPermesso>
                      )}
                    </div>
                  ),
                }))}
              />

              <Details summary={t('Come sono protetti i backup')}>
                <div className="md-stack">
                  <div className="md-banner" style={{ border: 'none', borderRadius: 'var(--md-sys-shape-corner-medium)' }}>
                    <Shield />
                    <div>
                      <div className="md-title-small">{t('Cifrati sempre, e nessuno può cancellarli dal server')}</div>
                      <div className="md-body-small" style={{ marginTop: 2 }}>
                        {t('Ogni backup è cifrato con una chiave che appartiene a questa organizzazione: Molo la custodisce e non la mostra a nessuno. Il server può solo {aggiungere} copie; quella che serve a cancellare le copie vecchie resta a Molo. Così un server compromesso non può distruggere la propria storia.', { aggiungere: <em>{t('aggiungere')}</em> })}
                      </div>
                    </div>
                  </div>
                  <DataTable
                    columns={[
                      { key: 'nome', header: t('Destinazione') },
                      { key: 'chiave', header: t('Impronta della chiave') },
                      { key: 'creata', header: t('Creata') },
                    ]}
                    rowKey={(r) => String(r.nome)}
                    rows={destinazioni.map((d) => ({
                      nome: d.label,
                      chiave: <span className="md-mono">…{d.repoKeyId}</span>,
                      creata: quandoRelativo(d.createdAt),
                    }))}
                  />
                </div>
              </Details>
            </section>
          )}
        </div>
      </div>

      <NuovaDestinazione slug={slug} open={apri} onOpenChange={setApri} />
    </>
  );
}

/**
 * L'esito dell'ultima prova. La riga che conta è la cancellazione *rifiutata*:
 * se riuscisse, la sola aggiunta non sarebbe configurata e la destinazione non
 * varrebbe come protezione dei record.
 */
function Prova({ destinazione }: { destinazione: BackupDestination }) {
  if (!destinazione.checkedAt) {
    return <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>{t('Mai provata')}</span>;
  }
  return (
    <div className="md-due-righe">
      <span>
        <StatusChip
          status={destinazione.checkOk ? 'ready' : 'error'}
          label={destinazione.checkOk ? t('Funziona') : t('Non funziona')}
        />
      </span>
      <span className="md-clamp">{quandoRelativo(destinazione.checkedAt)}{destinazione.checkError ? ` · ${destinazione.checkError}` : ''}</span>
    </div>
  );
}

/**
 * Le due credenziali sono chieste separatamente perché sono due permessi
 * diversi, e l'hub rifiuta di prenderle uguali. Con `hub_rest` non servono: le
 * ricava da sé.
 */
function NuovaDestinazione({
  slug, open, onOpenChange,
}: { slug: string; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<'hub_rest' | 's3' | 'b2' | 'sftp'>('hub_rest');
  const [endpoint, setEndpoint] = useState('');
  const [serverCreds, setServerCreds] = useState('');
  const [hubCreds, setHubCreds] = useState('');

  const esterna = kind !== 'hub_rest';
  const bucket = kind === 's3' || kind === 'b2';

  const crea = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/backup-destinations', {
          params: { path: { slug } },
          body: {
            label, kind, endpoint,
            ...(esterna ? { serverCreds, hubCreds } : {}),
          },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backup-destinations', slug] });
      onOpenChange(false);
      avvisa(t('{nome} è pronta a ricevere backup', { nome: label }));
      setLabel(''); setEndpoint(''); setServerCreds(''); setHubCreds('');
    },
  });

  const uguali = esterna && serverCreds !== '' && serverCreds === hubCreds;
  const pronto =
    label.trim() !== '' && endpoint.trim() !== '' && !crea.isPending && !uguali &&
    (!esterna || (serverCreds !== '' && hubCreds !== ''));

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Nuova destinazione')}
      description={t('Uno spazio riservato a questa organizzazione. La chiave che lo cifra la genera Molo e non la mostra a nessuno.')}
      actions={
        <>
          <Button variant="text" onClick={() => onOpenChange(false)}>{t('Annulla')}</Button>
          <Button variant="filled" icon={crea.isPending ? <Anello /> : undefined} disabled={!pronto} onClick={() => crea.mutate()}>
            {crea.isPending ? t('Preparo…') : t('Crea destinazione')}
          </Button>
        </>
      }
    >
      <div className="md-form">
        {crea.error && (
          <Banner tone="error" title={t('Non è stata creata')}>{(crea.error as Error).message}</Banner>
        )}

        <TextField
          label={t('Nome')}
          value={label}
          autoFocus
          onChange={(e) => setLabel(e.target.value)}
          help={t('Come vuoi chiamarla, es. «Backup principale».')}
        />

        <Select
          label={t('Tipo')}
          value={kind}
          options={[
            { value: 'hub_rest', label: t('Spazio di Molo — consigliato') },
            { value: 's3', label: t('S3 compatibile (AWS, Scaleway, MinIO…)') },
            { value: 'b2', label: 'Backblaze B2' },
            { value: 'sftp', label: t('Server SFTP') },
          ]}
          onChange={(e) => setKind(e.target.value as typeof kind)}
          help={esterna ? t('Servono due credenziali diverse: una per scrivere, una per pulire.') : t('Non serve altro: le credenziali le prepara Molo.')}
        />

        <TextField
          label={t('Indirizzo')}
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          placeholder={INDIRIZZO[kind]}
          help={
            bucket
              ? t('Endpoint S3, poi il bucket e un percorso con il nome dell’organizzazione (stile path, il bucket non va nell’host). Backblaze B2 si usa dal suo endpoint S3.')
              : t('Il percorso deve finire con il nome dell’organizzazione: uno spazio per azienda, mai condiviso.')
          }
        />

        {esterna && (
          <>
            <TextField
              label={t('Credenziale per scrivere')}
              value={serverCreds}
              onChange={(e) => setServerCreds(e.target.value)}
              error={uguali}
              help={bucket ? t('Nella forma «chiave:segreto». Va sul server: può scrivere e leggere, non cancellare.') : t('Va sul server: può aggiungere backup, non cancellarli.')}
            />
            <TextField
              label={t('Credenziale per pulire')}
              value={hubCreds}
              onChange={(e) => setHubCreds(e.target.value)}
              error={uguali}
              help={bucket ? t('Nella forma «chiave:segreto», con accesso pieno al bucket. Resta a Molo.') : t('Resta a Molo: elimina i backup scaduti e li verifica.')}
            />
            {bucket && (
              <Details summary={t('Come si prepara un bucket in sola aggiunta')}>
                <div className="md-stack">
                  <p className="md-body-small" style={{ margin: 0 }}>
                    {t('La credenziale del server ha bisogno di scrivere, leggere ed elencare il bucket, e di cancellare {solo} sotto {locks}: è dove restic mette il proprio lucchetto durante un backup e lo toglie alla fine. Su AWS, MinIO e simili è una policy come questa, con il nome del bucket e del percorso al posto dei segnaposto:', {
                      solo: <em>{t('solo')}</em>,
                      locks: <code>locks/</code>,
                    })}
                  </p>
                  <pre className="md-mono md-body-small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{POLICY_SOLA_AGGIUNTA}</pre>
                  <p className="md-body-small" style={{ margin: 0 }}>
                    {t('Su Backblaze B2, dove le policy non ci sono: una chiave applicativa limitata al bucket {senza} {deleteFiles} per il server, e una con {deleteFiles} per Molo. La prova qui sotto dice se la separazione regge.', {
                      senza: <b>{t('senza')}</b>,
                      deleteFiles: <code>deleteFiles</code>,
                    })}
                  </p>
                  {/* Imparato configurando la prima destinazione vera, nell'esercitazione
                      DR del 18/09/2026: quattro cose che la console di OVH non dice. */}
                  <p className="md-body-small" style={{ margin: 0 }}>
                    {t("{ovh}: le policy si importano {sullUtente} (Object Storage → Utenti → «Importa una policy»), senza {principal}: quella qui sopra va bene così com'è, per l'utente del server. L'utente creato dal wizard del container {non} ha permessi pieni: all'utente di Molo serve una policy con {s3} sul bucket, altrimenti la prova si ferma su «Access Denied» al primo passo. Le chiavi si generano con «Abilita Object Storage (S3)» e la secret si rilegge con «Mostra la chiave segreta». L'indirizzo è in stile {path}, con il bucket dopo l'host: {esempio}; quello con il bucket nell'host non funziona.", {
                      ovh: <b>{t('Su OVH Object Storage')}</b>,
                      sullUtente: <b>{t("sull'utente")}</b>,
                      principal: <code>Principal</code>,
                      non: <b>{t('non')}</b>,
                      s3: <code>s3:*</code>,
                      path: <em>path</em>,
                      esempio: <code>{t('https://s3.<regione>.io.cloud.ovh.net/<bucket>/<organizzazione>')}</code>,
                    })}
                  </p>
                </div>
              </Details>
            )}
            {uguali && (
              <Banner tone="error" title={t('Le due credenziali sono identiche')}>
                {t('Se il server potesse anche cancellare, un server compromesso cancellerebbe i propri backup. Servono due credenziali diverse.')}
              </Banner>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
